import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, X, Save } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ALL_ISLANDS } from "@/lib/islands";

const label = (v) => (ALL_ISLANDS.find((i) => i.v === v) || {}).l || v;

export function AdminPickupLocationsPanel() {
  const { t } = useTranslation();
  const [islands, setIslands] = useState([]);
  const [sel, setSel] = useState("");
  const [points, setPoints] = useState([]);
  const [fee, setFee] = useState(0);
  const [newPoint, setNewPoint] = useState("");
  const [saving, setSaving] = useState(false);

  const selectIsland = (d) => { setSel(d.island); setPoints(d.points || []); setFee(d.custom_fee || 0); setNewPoint(""); };

  const load = () => api.get("/admin/pickup-locations").then((r) => {
    setIslands(r.data.islands);
    setSel((cur) => {
      if (cur) return cur;
      if (r.data.islands.length) selectIsland(r.data.islands[0]);
      return cur;
    });
  }).catch((e) => toast.error(formatError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSelectChange = (v) => { const d = islands.find((x) => x.island === v); if (d) selectIsland(d); };
  const addPoint = () => { const p = newPoint.trim(); if (p && !points.includes(p)) { setPoints([...points, p]); setNewPoint(""); } };
  const removePoint = (p) => setPoints(points.filter((x) => x !== p));

  const save = async () => {
    setSaving(true);
    try { await api.put(`/admin/pickup-locations/${sel}`, { points, custom_fee: Number(fee) || 0 }); toast.success(t("pk.saved")); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl shadow-card border border-border p-6" data-testid="admin-pickup-panel">
      <h3 className="font-heading font-bold text-lg text-ink">{t("pk.title")}</h3>
      <p className="text-sm text-muted2 mb-4">{t("pk.sub")}</p>
      <div className="max-w-md space-y-4">
        <div>
          <label className="text-xs font-bold uppercase text-muted2">{t("pk.island")}</label>
          <select data-testid="admin-pickup-island" value={sel} onChange={(e) => onSelectChange(e.target.value)}
            className="w-full h-11 rounded-xl border border-border bg-sand px-3 text-sm font-semibold mt-1">
            {islands.map((d) => <option key={d.island} value={d.island}>{label(d.island)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-muted2">{t("pk.points")}</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {points.length === 0 && <p className="text-sm text-muted2">{t("pk.empty")}</p>}
            {points.map((p) => (
              <span key={p} className="inline-flex items-center gap-1 bg-pastel/50 text-canard-dark rounded-full px-3 py-1 text-xs font-bold">
                {p}<button type="button" data-testid={`admin-pickup-remove-${p}`} onClick={() => removePoint(p)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <Input data-testid="admin-pickup-newpoint" value={newPoint} onChange={(e) => setNewPoint(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPoint(); } }} placeholder={t("pk.pointPh")} className="h-10 rounded-xl bg-sand" />
            <Button type="button" data-testid="admin-pickup-add" onClick={addPoint} variant="outline" className="rounded-full shrink-0"><Plus className="w-4 h-4 mr-1" /> {t("pk.add")}</Button>
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-muted2">{t("pk.customFee")}</label>
          <Input data-testid="admin-pickup-fee" type="number" min="0" step="1" value={fee} onChange={(e) => setFee(e.target.value)} className="h-10 rounded-xl bg-sand mt-1 max-w-[160px]" />
          <p className="text-[11px] text-muted2 mt-1">{t("pk.customFeeHint")}</p>
        </div>
        <Button data-testid="admin-pickup-save" onClick={save} disabled={saving || !sel} className="rounded-full bg-canard hover:bg-canard-dark"><Save className="w-4 h-4 mr-1" /> {saving ? "..." : t("pk.save")}</Button>
      </div>
    </div>
  );
}
